import * as React from "react";
import { Table, Button, Dropdown, Menu, Modal, Tooltip, message } from "antd";
import { MoreOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import PromptForm from './PromptForm';
import { toasty } from "../../../../../Helper";
import { Pagination } from "antd";

const DataTable = ({
	rows,
	page,
	totalItems,
	handleSelectChange,
	handleEditRow,
	handleDeleteRow,
	handleChangePage,
	handleChangeRowsPerPage,
}) => {
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = React.useState(null);
	const [menuIndex, setMenuIndex] = React.useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [selectedRowId, setSelectedRowId] = useState(null);
	const [openPromptForm, setOpenPromptForm] = useState(false);
	const [selectedJobOpeningId, setSelectedJobOpeningId] = useState(null);
	const [openUpdatePromptForm, setOpenUpdatePromptForm] = useState(false);
	const [selectedUpdateJobOpeningId, setSelectedUpdateJobOpeningId] = useState(null);
	const [idTooltip, setIdTooltip] = React.useState("Copy ID");

	// Add debug log to see what data is being passed to DataTable
	React.useEffect(() => {
		console.log("DataTable received rows:", rows);
		console.log("DataTable received totalItems:", totalItems);
	}, [rows, totalItems]);

	const handleMenuOpen = (event, index) => {
		setAnchorEl(event.currentTarget);
		setMenuIndex(index);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setMenuIndex(null);
	};

	const isMenuOpen = Boolean(anchorEl);

	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(text).then(() => {
			console.log('ID copied to clipboard');
			// You can add a notification here if you want to inform the user that the ID was copied
		});
	};

	const handleDeleteClick = (id) => {
		setSelectedRowId(id);
		setOpenDialog(true);
	};

	const handleDialogClose = () => {
		setOpenDialog(false);
		setSelectedRowId(null);
	};

	const handleConfirmDelete = () => {
		handleDeleteRow(selectedRowId);
		handleDialogClose();
	};

	const handleCreatePromptClick = (id) => {
		setSelectedJobOpeningId(id);
		setOpenPromptForm(true);
	};

	const handlePromptFormClose = () => {
		setOpenPromptForm(false);
		setSelectedJobOpeningId(null);
	};

	const handleUpdatePromptClick = (id) => {
		setSelectedUpdateJobOpeningId(id);
		setOpenUpdatePromptForm(true);
	};

	const handleUpdatePromptFormClose = () => {
		setOpenUpdatePromptForm(false);
		setSelectedUpdateJobOpeningId(null);
	};

	const handleCopyId = (e, id) => {
		e.stopPropagation();
		navigator.clipboard.writeText(id).then(() => {
			setIdTooltip("Copied!");
			setTimeout(() => setIdTooltip("Copy ID"), 1500);
		});
		toasty("ID copied to clipboard!", "success");
	};

	// Add a safety check for rows
	const safeRows = Array.isArray(rows) ? rows : [];
	
	// Add debug log for safeRows
	React.useEffect(() => {
		console.log("DataTable safeRows:", safeRows);
	}, [safeRows]);

	// Add error handling for status
	const getStatusString = (status) => {
		if (typeof status === 'string') return status;
		if (status && typeof status === 'object') {
			return status.status || 'Unknown';
		}
		return 'Unknown';
	};

	const columns = [
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>ID</span>,
			dataIndex: 'id',
			render: (text) => (
				<>
					{text.slice(-10)}
					<Tooltip title={idTooltip}>
						<Button
							icon={<CopyOutlined />}
							size="small"
							onClick={(e) => handleCopyId(e, text)}
							style={{ marginLeft: 8 }}
						/>
					</Tooltip>
				</>
			),
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Created At</span>,
			dataIndex: 'created_at',
			render: (text) => {
				if (!text) return "N/A";
				const date = new Date(text);
				date.setHours(date.getHours() + 5);
				date.setMinutes(date.getMinutes() + 30);
				const day = String(date.getDate()).padStart(2, '0');
 	            const month = String(date.getMonth() + 1).padStart(2, '0'); // +1 because months are 0-based
 	           const year = date.getFullYear();
 
 	            return `${day}/${month}/${year}`;
 			},
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Organization</span>,
			dataIndex: 'organisation_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Department</span>,
			dataIndex: 'department_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Sub-Department</span>,
			dataIndex: 'subdepartment_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Role Name</span>,
			dataIndex: 'role_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Job Opening Name</span>,
			dataIndex: 'jobopening_name',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Salary Range</span>,
			dataIndex: 'salary_min',
			render: (_, record) => `${record.salary_min || 0} - ${record.salary_max || 0}`,
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>FT/PT</span>,
			dataIndex: 'employment_type',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Work Type</span>,
			dataIndex: 'work_arrangement',
			render: (text) => text || 'N/A',
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Positions</span>,
			dataIndex: 'no_open_positions',
			render: (text) => (typeof text === 'number' ? (text < 0 ? 0 : text) : 0),
		},
		{
			title: <span style={{ color: "#27272A", padding: "8px", fontSize: 11, fontWeight: '500' }}>Actions</span>,
			key: 'actions',
			render: (_, record) => (
				<Dropdown
					overlay={
						<Menu>
							<Menu.Item key="edit" onClick={(e) => {
								e.domEvent.stopPropagation(); // Prevent row click
								handleEditRow(record.id);
							}}>
								Edit
							</Menu.Item>
							<Menu.Item key="delete" onClick={(e) => {
								e.domEvent.stopPropagation(); // Prevent row click
								handleDeleteClick(record.id);
							}}>
								Delete
							</Menu.Item>
							<Menu.Item key="createPrompt" onClick={(e) => {
								e.domEvent.stopPropagation(); // Prevent row click
								handleCreatePromptClick(record.id);
							}}>
								Create Prompt
							</Menu.Item>
							<Menu.Item key="updatePrompt" onClick={(e) => {
								e.domEvent.stopPropagation(); // Prevent row click
								handleUpdatePromptClick(record.id);
							}}>
								Update Prompt
							</Menu.Item>
						</Menu>
					}
					trigger={["click"]}
				>
					<Button 
						icon={<MoreOutlined />} 
						onClick={(e) => e.stopPropagation()} 
					/>
				</Dropdown>
			),
		},
	];

	return (
		<div style={{margin:'0 20px'}}>
			{safeRows.length === 0 ? (
				<div style={{ 
					padding: '20px', 
					textAlign: 'center', 
					background: '#f9f9f9',
					borderRadius: '8px',
					marginBottom: '10px'
				}}>
					No job openings found. Try adjusting your filters.
				</div>
			) : (
				<Table
					columns={columns}
					dataSource={safeRows}
					rowKey="id"
					size="small"
					pagination={false}
					rowClassName="custom-row"
					scroll={{x:2000, y:'60vh'}}
					className="antTableClass"
					onRow={(record) => ({
						onClick: () => navigate(`/job-opening-details/${record.id}`),
						style: { cursor: 'pointer'}
					})}
				/>
			)}

			<div style={{ 
				display: 'flex', 
				justifyContent: 'space-between', 
				alignItems: 'center',
				padding: '16px 8px',
				background: 'rgba(238, 238, 240, 1)',
				borderTop: '0.5px solid rgba(218, 218, 221, 1)',
				borderRadius: '0 0 8px 8px',
				marginTop: safeRows.length === 0 ? '0' : '-10px'
			}}>
				<div style={{ 
					fontFamily: 'Inter', 
					fontSize: '12px', 
					fontWeight: 400, 
					lineHeight: '18px', 
					letterSpacing: '-0.1px', 
					color: '#27272A' 
				}}>
					{safeRows.length === 0 
						? `No results found. Total: ${totalItems} Job Openings` 
						: `Showing ${Math.min(safeRows.length, 15)} of ${totalItems} Job Openings`
					}
				</div>

				<Pagination
					current={page}
					total={totalItems}
					pageSize={15}
					showSizeChanger={false}
					size="small"
					style={{ margin: '0 auto' }}
					onChange={handleChangePage}
					itemRender={(_, type, originalElement) => {
						if (type === 'prev' || type === 'next') {
							return null;
						}
						return originalElement;
					}}
					className="custom-pagination"
				/>

				<div style={{ display: 'flex', alignItems: 'center' }}>
					<div 
						onClick={() => handleChangePage(page - 1)}
						style={{ 
							fontFamily: 'Inter', 
							fontSize: '12px', 
							fontWeight: 400, 
							lineHeight: '18px', 
							letterSpacing: '-0.1px', 
							color: page > 1 ? '#666' : '#ccc', 
							cursor: page > 1 ? 'pointer' : 'not-allowed', 
							marginRight: '0px',
							paddingRight: '16px' 
						}}
					>
						← Previous
					</div>
					<div 
						style={{ 
							borderLeft: '1px solid #d9d9d9',
							marginLeft: '8px',
							paddingLeft: '16px'
						}}
					>
						<div 
							onClick={() => handleChangePage(page + 1)}
							style={{ 
								fontFamily: 'Inter', 
								fontSize: '12px', 
								fontWeight: 400, 
								lineHeight: '18px', 
								letterSpacing: '-0.1px', 
								color: page < Math.ceil(totalItems / 15) ? '#666' : '#ccc', 
								cursor: page < Math.ceil(totalItems / 15) ? 'pointer' : 'not-allowed' 
							}}
						>
							Next →
						</div>
					</div>
				</div>
			</div>

			<Modal
				title="Confirm Deletion"
				open={openDialog}
				onCancel={handleDialogClose}
				onOk={handleConfirmDelete}
				okText="Confirm"
				cancelText="Cancel"
			>
				<p>Are you sure you want to delete this job opening?</p>
			</Modal>

			<PromptForm 
				open={openPromptForm} 
				onClose={handlePromptFormClose} 
				jobOpeningId={selectedJobOpeningId} 
				isUpdate={false}
			/>

			<PromptForm 
				open={openUpdatePromptForm} 
				onClose={handleUpdatePromptFormClose} 
				jobOpeningId={selectedUpdateJobOpeningId}
				isUpdate={true}
			/>
		</div>
	);
};

export default DataTable;

<style jsx global>{`
	.custom-pagination .ant-pagination-item {
		border: 1px solid lightblue !important;
	}
	.custom-pagination .ant-pagination-item-active {
		border: 1px solid lightblue !important;
		background-color: #f0f0f0 !important;
	}
	.custom-pagination .ant-pagination-item:focus,
	.custom-pagination .ant-pagination-item:hover {
		border: 1px solid lightblue !important;
	}
	.custom-pagination .ant-pagination-item a {
		color: #666 !important;
	}
`}</style>